import { ChevronIcon } from "vega-icons";

import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ThemedButton } from "../Buttons/ThemedButton";

import { NoticeType, topBarTypeConfig } from "./noticeConfig";
import { type TopBarNoticeProps } from "./types";

export const NoticeTopBar = ({
  content,
  details,
  expandable = true,
  expandedByDefault = false,
  type = NoticeType.BRAND,
  sticky = false,
  className,
  icon,
  leftIcon,
  rightIcon,
  onClose,
  dataTestId,
}: TopBarNoticeProps) => {
  const { value: expanded, toggle } = useBoolean(expandedByDefault);

  const {
    iconComponent: DefaultIconComponent,
    iconColor,
    bgColor,
  } = topBarTypeConfig[type];

  const handleToggle = () => {
    if (details && expandable) {
      toggle();
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  const hasExpandableDetails = expandable && details;

  return (
    <div
      className={cnTwMerge(
        "top-0 left-0 right-0 z-40 min-h-navbar p-3 rounded-companyBorderRadius backdrop-bg flex flex-col justify-center",
        sticky ? "sticky" : "fixed",
        bgColor,
        className,
      )}
      data-testid={dataTestId}
    >
      <div
        className={cnTwMerge("flex items-center w-full justify-between gap-2", {
          "cursor-pointer": hasExpandableDetails,
        })}
        onClick={handleToggle}
      >
        <div className="flex min-w-0 items-center gap-2">
          {!!leftIcon && (
            <>
              <div>
                <ThemedButton
                  className="cursor-pointer pointer-events-auto"
                  dataTestId="close-button"
                  icon={leftIcon}
                  onClick={handleClose}
                  variant="tertiary"
                />
              </div>
              <div className="h-6 w-px bg-border/primary" />
            </>
          )}
          {icon ? (
            <span className={iconColor}>{icon}</span>
          ) : (
            <DefaultIconComponent className={iconColor} />
          )}
          <span
            className={cnTwMerge(
              "min-w-0 text-text/primary text-left",
              expanded ? "body-base-semibold" : "body-base-medium",
            )}
            data-testid="banner-content"
          >
            {content}
          </span>
        </div>

        <div className="flex items-center">
          {hasExpandableDetails ? (
            <ChevronIcon
              className={cnTwMerge(
                "defaultTransition text-text/secondary cursor-pointer",
                {
                  "rotate-180 text-text/primary": expanded,
                },
              )}
            />
          ) : null}
          {!!rightIcon && (
            <>
              <div className="h-6 w-px bg-border/primary" />
              <div className="mx-4">
                <ThemedButton
                  className="cursor-pointer pointer-events-auto"
                  dataTestId="close-button"
                  icon={rightIcon}
                  onClick={handleClose}
                  variant="tertiary"
                />
              </div>
            </>
          )}
        </div>
      </div>
      {details && expanded ? <div className="pt-2">{details}</div> : null}
    </div>
  );
};
